import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { usePage } from '../contexts/CmsContext';
import { Users, GraduationCap, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Maps display names shown on the Faculty page accordion to the department
// values used in the Teaching Staff records (which come from the official
// CSV upload). Any Faculty department not covered here will render as 0.
const STAFF_DEPT_ALIASES = {
  'Anatomy': ['Anatomy'],
  'Physiology': ['Physiology'],
  'Biochemistry': ['Biochemistry'],
  'Pharmacology': ['Pharmacology'],
  'Pathology': ['Pathology'],
  'Microbiology': ['Microbiology'],
  'Community Medicine': ['Community Medicine'],
  'Forensic Medicine': ['Forensic Medicine'],
  'General Medicine': ['General Medicine'],
  'Paediatrics': ['Paediatrics', 'Pediatrics'],
  'Dermatology & Venereology': ['Dermatology', 'Dermatology & Venereology'],
  'Psychiatry': ['Psychiatry'],
  'General Surgery': ['General Surgery'],
  'Orthopaedics': ['Orthopedics', 'Orthopaedics'],
  'Oto-Rhino-Laryngology (ENT)': ['ENT', 'Oto-Rhino-Laryngology'],
  'Ophthalmology': ['Ophthalmology'],
  'Obst. & Gynaecology': ['Obs & Gynae', 'Obst. & Gynaecology', 'Obstetrics & Gynaecology'],
  'Anaesthesiology': ['Anaesthesia', 'Anaesthesiology', 'Anesthesia'],
  'Radio-Diagnosis': ['Radiology', 'Radio-Diagnosis'],
  'Dentistry': ['Dentistry'],
  'Respiratory Medicine': ['Respiratory Medicine', 'Pulmonary Medicine', 'TB & Chest'],
  'Oncology / Cancer': ['Radiation Oncology', 'Oncology', 'Medical Oncology'],
  'Neurology': ['Neurology'],
  'Cardiology': ['Cardiology'],
  'Urology (Medicine & Surgery)': ['Urology'],
  'Paediatric Surgery': ['Paediatric Surgery', 'Pediatric Surgery'],
};

const FACULTY_HERO = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&q=80";

const departments = [
  {
    name: 'Pre-Clinical Departments',
    departments: [
      { name: 'Anatomy', facultyCount: '---', hod: 'Senior Faculty – Department of Anatomy' },
      { name: 'Physiology', facultyCount: '---', hod: 'Senior Faculty – Department of Physiology' },
      { name: 'Biochemistry', facultyCount: '---', hod: 'Senior Faculty – Department of Biochemistry' },
    ],
  },
  {
    name: 'Para-Clinical Departments',
    departments: [
      { name: 'Pharmacology', facultyCount: '---', hod: 'Senior Faculty – Department of Pharmacology' },
      { name: 'Pathology', facultyCount: '---', hod: 'Senior Faculty – Department of Pathology' },
      { name: 'Microbiology', facultyCount: '---', hod: 'Senior Faculty – Department of Microbiology' },
      { name: 'Community Medicine', facultyCount: '---', hod: 'Senior Faculty – Department of Community Medicine' },
      { name: 'Forensic Medicine', facultyCount: '---', hod: 'Senior Faculty – Department of Forensic Medicine' },
    ],
  },
  {
    name: 'Clinical Departments',
    departments: [
      { name: 'General Medicine', facultyCount: '---', hod: 'Senior Faculty – Department of General Medicine' },
      { name: 'Paediatrics', facultyCount: '---', hod: 'Senior Faculty – Department of Paediatrics' },
      { name: 'Dermatology & Venereology', facultyCount: '---', hod: 'Senior Faculty – Department of Dermatology & Venereology' },
      { name: 'Psychiatry', facultyCount: '---', hod: 'Senior Faculty – Department of Psychiatry' },
      { name: 'General Surgery', facultyCount: '---', hod: 'Senior Faculty – Department of General Surgery' },
      { name: 'Orthopaedics', facultyCount: '---', hod: 'Senior Faculty – Department of Orthopaedics' },
      { name: 'Oto-Rhino-Laryngology (ENT)', facultyCount: '---', hod: 'Senior Faculty – Department of ENT' },
      { name: 'Ophthalmology', facultyCount: '---', hod: 'Senior Faculty – Department of Ophthalmology' },
      { name: 'Obst. & Gynaecology', facultyCount: '---', hod: 'Senior Faculty – Department of Obst. & Gynaecology' },
      { name: 'Anaesthesiology', facultyCount: '---', hod: 'Senior Faculty – Department of Anaesthesiology' },
      { name: 'Radio-Diagnosis', facultyCount: '---', hod: 'Senior Faculty – Department of Radio-Diagnosis' },
      { name: 'Dentistry', facultyCount: '---', hod: 'Senior Faculty – Department of Dentistry' },
      { name: 'Respiratory Medicine', facultyCount: '---', hod: 'Senior Faculty – Department of Respiratory Medicine' },
    ],
  },
  {
    name: 'Super Specialty Departments',
    departments: [
      { name: 'Oncology / Cancer', facultyCount: '---', hod: 'Senior Faculty – Department of Oncology' },
      { name: 'Neurology', facultyCount: '---', hod: 'Senior Faculty – Department of Neurology' },
      { name: 'Cardiology', facultyCount: '---', hod: 'Senior Faculty – Department of Cardiology' },
      { name: 'Urology (Medicine & Surgery)', facultyCount: '---', hod: 'Senior Faculty – Department of Urology' },
      { name: 'Paediatric Surgery', facultyCount: '---', hod: 'Senior Faculty – Department of Paediatric Surgery' },
    ],
  },
];

const Faculty = () => {
  const { data: facultyCms } = usePage('faculty');
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/cms/teaching-staff`)
      .then(r => r.json())
      .then(d => setStaff(d.staff || []))
      .catch(() => {});
  }, []);

  const deptCounts = useMemo(() => {
    const counts = {};
    staff.forEach(s => {
      const key = (s.department || '').trim().toLowerCase();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [staff]);

  const countForDept = (facultyName) => {
    const aliases = STAFF_DEPT_ALIASES[facultyName] || [facultyName];
    let total = 0;
    aliases.forEach(a => { total += deptCounts[a.trim().toLowerCase()] || 0; });
    return total;
  };

  const totalFaculty = facultyCms?.total_faculty ?? '---';
  const totalDepartments = facultyCms?.total_departments ?? '26';
  const supportingStaff = facultyCms?.supporting_staff ?? '---';
  return (
    <div className="page-transition" data-testid="faculty-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${FACULTY_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="faculty-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Our Team
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Faculty & Staff
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Meet our distinguished faculty members who bring expertise, dedication, and passion to medical education.
            </p>
          </div>
        </div>
      </section>

      {/* Overview Stats */}
      <section className="py-12 bg-white border-b border-gray-100" data-testid="faculty-stats">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6" data-testid="faculty-stat-total">
              <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-[#4ECDC4]" size={24} />
              </div>
              <p className="font-heading text-3xl font-bold text-[#1A202C]" data-testid="faculty-stat-total-value">{totalFaculty}</p>
              <p className="text-sm text-gray-600 mt-1">Total Faculty Members</p>
            </div>
            <div className="text-center p-6" data-testid="faculty-stat-departments">
              <div className="w-14 h-14 bg-[#D4A84B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="text-[#D4A84B]" size={24} />
              </div>
              <p className="font-heading text-3xl font-bold text-[#1A202C]" data-testid="faculty-stat-departments-value">{totalDepartments}</p>
              <p className="text-sm text-gray-600 mt-1">Academic Departments</p>
            </div>
            <div className="text-center p-6" data-testid="faculty-stat-supporting">
              <div className="w-14 h-14 bg-[#9ACD32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-[#9ACD32]" size={24} />
              </div>
              <p className="font-heading text-3xl font-bold text-[#1A202C]" data-testid="faculty-stat-supporting-value">{supportingStaff}</p>
              <p className="text-sm text-gray-600 mt-1">Supporting Staff</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dean / Leadership */}
      <section className="section-padding bg-white" data-testid="faculty-dean">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Leadership
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C]">
                Meet Our Dean
              </h2>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-8 bg-gradient-to-br from-[#F8F9FA] to-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100">
              <div className="flex-shrink-0">
                <div className="w-48 h-60 rounded-xl overflow-hidden shadow-xl border-4 border-[#D4A84B]/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/v0kgpsrn_Dean%20Dr%20Kasar%202.jpg"
                    alt="Dr. Pradeep Kumar Kasar - Dean, SSMCH"
                    className="w-full h-full object-cover object-[25%_top]"
                    data-testid="dean-photo-faculty"
                  />
                </div>
              </div>
              <div className="text-center md:text-left">
                <h3 className="font-heading text-2xl font-bold text-[#1A202C] mb-1">
                  Dr. Pradeep Kumar Kasar
                </h3>
                <p className="text-sm text-gray-500 mb-1">MBBS, MD (Community Medicine)</p>
                <p className="text-[#D4A84B] font-semibold mb-4">Dean, Sukh Sagar Medical College & Hospital</p>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Dr. Kasar is a Community Physician expert in Preventing Diseases, Epidemiology, Hospital Administration and Management. He has wide knowledge in Research Methodology and Biostatistics, and has offered expert medical advice and guidance to countless patients across Madhya Pradesh.
                </p>
                <div className="text-sm text-gray-500 space-y-1">
                  <p>Former Dean, NSCB Medical College</p>
                  <p>Former Medical Superintendent, NSCB Medical College and Cancer Hospital</p>
                  <p className="text-xs text-gray-400 mt-2">At SSMCH since 1st July 2022</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Faculty Overview */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="faculty-overview">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
              About Our Faculty
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Excellence in Teaching
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Our faculty comprises experienced medical professionals and academicians who are committed to providing quality education. Each department is headed by qualified specialists with extensive experience in teaching, research, and clinical practice.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-[#1A202C] text-white rounded-t-xl">
                <CardTitle className="font-heading text-xl">Department-wise Faculty</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Accordion type="single" collapsible className="w-full">
                  {departments.map((category, catIndex) => (
                    <AccordionItem key={catIndex} value={`category-${catIndex}`}>
                      <AccordionTrigger className="px-6 py-4 hover:bg-gray-50 text-left">
                        <span className="font-heading text-lg font-semibold text-[#1A202C]">
                          {category.name}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="px-6 pb-4">
                          <div className="space-y-3">
                            {category.departments.map((dept, deptIndex) => {
                              const count = countForDept(dept.name);
                              return (
                              <div 
                                key={deptIndex}
                                className="p-4 bg-gray-50 rounded-lg"
                                data-testid={`dept-${catIndex}-${deptIndex}`}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <div>
                                    <h4 className="font-semibold text-[#1A202C]">{dept.name}</h4>
                                    <p className="text-sm text-gray-600">{dept.hod}</p>
                                  </div>
                                  <div className="text-right">
                                    <span
                                      className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-3 py-1 rounded-full text-sm font-medium"
                                      data-testid={`dept-count-${catIndex}-${deptIndex}`}
                                    >
                                      {count} Faculty
                                    </span>
                                  </div>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A202C]" data-testid="faculty-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Learn from the Best
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Our experienced faculty is dedicated to shaping the next generation of healthcare professionals.
          </p>
          <Link to="/admissions">
            <Button 
              className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
              data-testid="faculty-apply-btn"
            >
              Apply for Admission
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Faculty;
